'use client'

/**
 * ORYXOS Security Provider — React context for client-side security
 *
 * Features:
 *  - Clears sensitive storage on mount/unmount
 *  - Adds beforeunload listener to clear data
 *  - Detects and prevents XSS attempts in URL parameters
 *  - Sets up Content Security Policy violation reporting
 *  - Provides reportSecurityViolation() function to children
 *  - Logs client-side security events to API
 *  - Wraps children in a security context
 */

import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react'
import {
  clearSensitiveStorage,
  detectXssAttempt,
  preventClickjacking,
  isSafeUrl,
} from '@/lib/security/client-security'

// ── Types ─────────────────────────────────────────────────────────────

interface SecurityViolation {
  type: 'csp' | 'xss' | 'clickjacking' | 'unsafe_url' | 'storage' | 'other'
  details: string
  url?: string
  timestamp: string
}

interface SecurityContextValue {
  /** Report a security violation to the backend */
  reportSecurityViolation: (violation: Omit<SecurityViolation, 'timestamp'>) => void
}

// ── Context ───────────────────────────────────────────────────────────

const SecurityContext = createContext<SecurityContextValue | null>(null)

/**
 * Hook to access the security context.
 * Must be used within a SecurityProvider.
 */
export function useSecurity(): SecurityContextValue {
  const ctx = useContext(SecurityContext)
  if (!ctx) {
    throw new Error('useSecurity must be used within a SecurityProvider')
  }
  return ctx
}

// ── Provider Component ────────────────────────────────────────────────

interface SecurityProviderProps {
  children: React.ReactNode
}

export function SecurityProvider({ children }: SecurityProviderProps) {
  const cleanedRef = useRef(false)
  const cspListenerRef = useRef(false)

  // Report security violation to the backend
  const reportSecurityViolation = useCallback(
    async (violation: Omit<SecurityViolation, 'timestamp'>) => {
      const fullViolation: SecurityViolation = {
        ...violation,
        timestamp: new Date().toISOString(),
      }

      // Log to console for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ORYXOS Security] Violation detected:', fullViolation)
      }

      // Skip reporting on unauthenticated pages (login, register, landing)
      if (typeof window !== 'undefined') {
        const url = window.location.href
        if (url.includes('view=login') || url.includes('view=register') || url.includes('view=landing') || url.includes('/login')) return
      }

      // Send to backend API
      try {
        await fetch('/api/cpanel/security/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventType: `client_${violation.type}`,
            details: JSON.stringify(fullViolation),
          }),
        })
      } catch {
        // Don't let reporting failures break the app
      }
    },
    []
  )

  // ── On Mount: Clear sensitive storage, detect XSS in URL, set up CSP ──
  useEffect(() => {
    // 1. Clear sensitive storage from previous sessions
    clearSensitiveStorage()

    // 2. Detect XSS attempts in URL parameters
    try {
      const url = new URL(window.location.href)
      const searchParams = url.searchParams

      for (const [key, value] of searchParams.entries()) {
        const xssCheck = detectXssAttempt(value)
        if (xssCheck.detected) {
          reportSecurityViolation({
            type: 'xss',
            details: `XSS pattern detected in URL parameter "${key}": ${xssCheck.patterns.join(', ')}`,
            url: window.location.href,
          })

          // Remove the dangerous parameter from the URL
          searchParams.delete(key)
        }

        // Also check the key name itself
        const keyXssCheck = detectXssAttempt(key)
        if (keyXssCheck.detected) {
          reportSecurityViolation({
            type: 'xss',
            details: `XSS pattern detected in URL parameter name: ${keyXssCheck.patterns.join(', ')}`,
            url: window.location.href,
          })
        }
      }

      // Also check hash fragment
      if (url.hash && detectXssAttempt(url.hash).detected) {
        reportSecurityViolation({
          type: 'xss',
          details: 'XSS pattern detected in URL hash fragment',
          url: window.location.href,
        })
      }
    } catch {
      // URL parsing may fail
    }

    // 3. Check for clickjacking
    const { isFramed } = preventClickjacking()
    if (isFramed) {
      reportSecurityViolation({
        type: 'clickjacking',
        details: 'Page is being rendered inside an iframe',
        url: window.location.href,
      })
    }

    // 4. Set up CSP violation reporting
    if (!cspListenerRef.current && typeof document !== 'undefined') {
      cspListenerRef.current = true
      document.addEventListener('securitypolicyviolation', (event) => {
        reportSecurityViolation({
          type: 'csp',
          details: `CSP violation: ${event.violatedDirective} - ${event.blockedURI}`,
          url: event.documentURI,
        })
      })
    }

    // 5. Validate any redirects (check if current URL is safe)
    if (!isSafeUrl(window.location.href)) {
      reportSecurityViolation({
        type: 'unsafe_url',
        details: 'Current page URL uses unsafe protocol',
        url: window.location.href,
      })
    }
  }, [reportSecurityViolation])

  // ── beforeunload: Clear sensitive data on tab close ──
  useEffect(() => {
    const handleUnload = () => {
      if (!cleanedRef.current) {
        cleanedRef.current = true
        clearSensitiveStorage()
      }
    }

    window.addEventListener('beforeunload', handleUnload)

    return () => {
      window.removeEventListener('beforeunload', handleUnload)
      // Clean up on unmount as well
      handleUnload()
    }
  }, [])

  const contextValue: SecurityContextValue = {
    reportSecurityViolation,
  }

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  )
}
